import React, { useEffect, useMemo, useState } from 'react';
import { SOURCE_LABELS } from './constants';
import styles from './ProductFinder.module.css';

const PAGE_SIZE = 50;
const NA = 'N/A';

const fmtMoney = (cur, v) => `${cur}${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

const csvCell = (v) => {
  const s = v === null || v === undefined ? '' : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const download = (name, mime, text) => {
  const url = URL.createObjectURL(new Blob([text], { type: mime }));
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

// KPIs, the Helium 10 layout table (only Landed Cost editable), token breakdown,
// product detail with the fee breakdown, and CSV / JSON downloads.
const ResultsView = ({ search, onLandedCost, recomputing }) => {
  const summary = search.summary || {};
  const tab = search.tab;
  const cur = summary.currency || '₹';
  const rows = useMemo(() => search.rows || [], [search.rows]);
  const details = useMemo(() => search.details || [], [search.details]);
  const overrides = search.landed_overrides || {};

  const [page, setPage] = useState(0);
  const [sort, setSort] = useState(null); // { col, dir }
  const [detailAsin, setDetailAsin] = useState('');
  const [exact, setExact] = useState('');

  useEffect(() => { setPage(0); }, [search.id, sort]);
  useEffect(() => {
    if (details.length && !details.some((d) => d.asin === detailAsin)) setDetailAsin(details[0].asin);
  }, [details, detailAsin]);

  const detailByAsin = useMemo(() => Object.fromEntries(details.map((d) => [d.asin, d])), [details]);
  const detail = detailByAsin[detailAsin];
  useEffect(() => {
    setExact(detail && detail.margin_info ? String(detail.margin_info.landed_cost ?? '') : '');
  }, [detail]);

  const sorted = useMemo(() => {
    if (!sort) return rows;
    const val = (r) => {
      const v = r[sort.col];
      return v === NA || v === null || v === undefined ? null : v;
    };
    return [...rows].sort((a, b) => {
      const x = val(a);
      const y = val(b);
      if (x === null) return 1;
      if (y === null) return -1;
      const c = typeof x === 'number' && typeof y === 'number' ? x - y : String(x).localeCompare(String(y));
      return sort.dir === 'asc' ? c : -c;
    });
  }, [rows, sort]);

  const pages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const pageRows = sorted.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const columns = search.columns || [];

  const n = details.length;
  const scoreKey = tab === 'active' ? 'opportunity_score' : 'gap_score';
  const topScore = n ? Math.max(...details.map((d) => d[scoreKey] || 0)) : 0;
  const avgMargin = n ? details.reduce((s, d) => s + (d.margin_pct || 0), 0) / n : 0;
  const totalRev = details.reduce((s, d) => s + ((tab === 'active' ? d.revenue_mid : d.lost_revenue) || 0), 0);
  const revLabel = summary.domain === 10 ? `₹${(totalRev / 100000).toFixed(1)} Lakh` : fmtMoney(cur, Math.round(totalRev));

  const setOverride = (asin, value) => {
    const next = { ...overrides };
    if (value === null) delete next[asin];
    else next[asin] = value;
    onLandedCost(undefined, next);
  };

  const editCell = (asin, raw, original) => {
    if (raw === '' || raw === null) return;
    const v = Number(raw);
    if (Number.isNaN(v) || v < 0) return;
    if (original !== NA && Math.abs(v - Number(original)) <= 0.004) return;
    setOverride(asin, v);
  };

  const stamp = new Date().toISOString().replace(/[-:T]/g, '').slice(0, 14);
  const fileBase = `product_finder_${(summary.marketplace_name || 'amazon').replace(/\s+/g, '_').toLowerCase()}_${tab}_${stamp}`;
  const downloadCsv = () => {
    const lines = [columns.map(csvCell).join(',')].concat(rows.map((r) => columns.map((c) => csvCell(r[c])).join(',')));
    download(`${fileBase}.csv`, 'text/csv;charset=utf-8', `﻿${lines.join('\n')}`);
  };
  const downloadJson = () => download(`${fileBase}.json`, 'application/json', JSON.stringify(rows, null, 2));

  const breakdown = summary.token_breakdown || [];
  const totalTokens = summary.tokens_used_search ?? breakdown.reduce((s, r) => s + r.tokens, 0);

  const renderCell = (row, col) => {
    const v = row[col];
    if (col === 'URL') return v ? <a href={v} target="_blank" rel="noopener noreferrer">Open</a> : '—';
    if (col === 'Image URL') return v && v !== NA ? <img src={v} alt="" className={styles.thumb} loading="lazy" /> : '—';
    if (col === 'Landed Cost') {
      return (
        <input
          key={`${row.ASIN}-${v}`}
          type="number"
          min="0"
          step="1"
          className={styles.cellInput}
          defaultValue={v === NA ? '' : v}
          disabled={recomputing}
          onBlur={(e) => editCell(row.ASIN, e.target.value, v)}
          onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
        />
      );
    }
    if (col === 'ASIN') {
      return <button type="button" className={styles.linkBtn} onClick={() => setDetailAsin(v)}>{v}</button>;
    }
    return v === NA || v === null || v === undefined ? '—' : String(v);
  };

  return (
    <div className={styles.results}>
      {summary.stopped_reason && <p className={styles.warn}>{summary.stopped_reason}</p>}
      {(summary.warnings || []).map((w) => <p key={w} className={styles.caption}>ℹ️ {w}</p>)}
      {!rows.length && (
        <p className={styles.info}>
          No products passed your filters after scanning {summary.scanned_count || 0}. Try loosening a filter.
        </p>
      )}
      {rows.length > 0 && rows.length < (summary.target_count || 200) && !summary.stopped_reason && (
        <p className={styles.caption}>
          {rows.length} of {summary.target_count} products passed after scanning {summary.scanned_count} (Keepa matched{' '}
          {(summary.total_matches || 0).toLocaleString()}). Loosen a filter or raise “Most products to scan” for more.
        </p>
      )}
      {details.some((d) => d.scores_provisional) && (
        <p className={styles.caption}>
          ℹ️ Scores are provisional for categories with fewer than 200 products seen so far; they firm up as you search more.
        </p>
      )}

      {rows.length > 0 && (
        <>
          <div className={styles.kpis}>
            <div className={styles.kpi}><span>Products</span><strong>{n}</strong></div>
            <div className={styles.kpi}><span>{tab === 'active' ? 'Top Opp. Score' : 'Top Gap Score'}</span><strong>{topScore.toFixed(1)}</strong></div>
            <div className={styles.kpi}><span>Avg. Net Margin</span><strong>{avgMargin.toFixed(1)}%</strong></div>
            <div className={styles.kpi}><span>{tab === 'active' ? 'Combined Monthly Rev' : 'Lost Monthly Demand'}</span><strong>{revLabel}</strong></div>
            <div className={styles.kpi}><span>Tokens (this search)</span><strong>{totalTokens}</strong></div>
          </div>

          <p className={styles.caption}>
            Click an ASIN for its fee breakdown. Type your real per-unit cost in <b>Landed Cost ✏️</b>: margin and scores
            update without using Keepa tokens{recomputing ? ' (updating…)' : ''}.
          </p>
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  {columns.map((c) => (
                    <th
                      key={c}
                      onClick={() => setSort((s) => (s && s.col === c ? { col: c, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { col: c, dir: 'desc' }))}
                    >
                      {c === 'Image URL' ? 'Image' : c}{c === 'Landed Cost' ? ` (${cur.trim()}) ✏️` : ''}
                      {sort && sort.col === c ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ''}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pageRows.map((r) => (
                  <tr key={r.ASIN} className={r.ASIN === detailAsin ? styles.rowPicked : undefined}>
                    {columns.map((c) => (
                      <td key={c} className={c === 'Title' || c === 'Why' || c === 'Flags' ? styles.wideCell : undefined}>
                        {renderCell(r, c)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {pages > 1 && (
            <div className={styles.pager}>
              <button type="button" className={styles.secondaryBtn} disabled={page === 0} onClick={() => setPage(page - 1)}>‹ Prev</button>
              <span>Page {page + 1} of {pages}</span>
              <button type="button" className={styles.secondaryBtn} disabled={page >= pages - 1} onClick={() => setPage(page + 1)}>Next ›</button>
            </div>
          )}

          {detail && (
            <section className={styles.detail}>
              <h4 className={styles.colTitle}>
                Product detail · {detail.asin} · {(detail.title || '').slice(0, 80)}
              </h4>
              <div className={styles.detailGrid}>
                <div>
                  <h5>Fees &amp; margin</h5>
                  <ul>
                    <li>Selling price: {fmtMoney(cur, detail.margin_info?.selling_price)}</li>
                    <li>Referral fee: {fmtMoney(cur, detail.margin_info?.referral_fee)}</li>
                    {detail.domain === 10 && (
                      <>
                        <li>Closing fee: {fmtMoney(cur, detail.margin_info?.closing_fee)}</li>
                        <li>Weight handling: {fmtMoney(cur, detail.margin_info?.weight_handling_fee)}</li>
                        <li>FBA pick &amp; pack: {fmtMoney(cur, detail.margin_info?.pick_and_pack_fee)}</li>
                      </>
                    )}
                    <li>Total fees{detail.domain === 10 ? ' (incl. 18% GST)' : ''}: <b>{fmtMoney(cur, detail.margin_info?.fees_with_gst)}</b></li>
                    <li>Landed cost: {fmtMoney(cur, detail.margin_info?.landed_cost)} ({detail.landed_cost_source})</li>
                    <li>Net profit / unit: <b>{fmtMoney(cur, detail.margin_info?.net_profit)}</b> · margin <b>{detail.margin_pct}%</b></li>
                  </ul>
                </div>
                <div>
                  <h5>Exact landed cost</h5>
                  <label className={styles.field}>
                    <span className={styles.label}>Landed cost per unit ({cur.trim()})</span>
                    <input type="number" min="0" step="5" className={styles.input} value={exact}
                      onChange={(e) => setExact(e.target.value)} />
                  </label>
                  <div className={styles.inlineForm}>
                    <button type="button" className={styles.primaryBtnSmall} disabled={recomputing || exact === ''}
                      onClick={() => setOverride(detail.asin, Number(exact))}>Apply</button>
                    <button type="button" className={styles.secondaryBtn} disabled={recomputing || !(detail.asin in overrides)}
                      onClick={() => setOverride(detail.asin, null)}>Use default %</button>
                  </div>
                </div>
                <div>
                  <h5>Listing &amp; competition</h5>
                  <ul>
                    <li>Category: <i>{detail.category_path || detail.category_name}</i></li>
                    <li>Listing weakness: <b>{detail.listing_weakness || 0} / 100</b></li>
                    <li>Images: {detail.image_count || 0} · A+: {detail.has_aplus ? '✅' : '❌'} · Video: {detail.has_video ? '✅' : '❌'}</li>
                    <li>Rating: {detail.rating || 0} ⭐ ({detail.review_count || 0} reviews)</li>
                    <li>Sellers now: {detail.sellers_now || 0} · Amazon share 90 d: {Math.round((detail.amazon_selling_share_90d || 0) * 100)}%</li>
                    <li>Est. units / mo: {detail.units_mid} ({detail.units_low}–{detail.units_high})</li>
                    <li><a href={detail.amazon_url} target="_blank" rel="noopener noreferrer">Open on Amazon</a></li>
                  </ul>
                </div>
              </div>
            </section>
          )}

          <div className={styles.inlineForm}>
            <button type="button" className={styles.secondaryBtn} onClick={downloadCsv}>Download CSV</button>
            <button type="button" className={styles.secondaryBtn} onClick={downloadJson}>Download JSON</button>
          </div>
        </>
      )}

      {breakdown.length > 0 && (
        <section className={styles.detail}>
          <h4 className={styles.colTitle}>Token breakdown</h4>
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr><th>Step</th><th>Keepa requests</th><th>Items</th><th>Tokens</th><th>Tokens / item</th><th>Share</th><th>Source</th></tr>
              </thead>
              <tbody>
                {breakdown.map((r) => (
                  <tr key={`${r.step}-${r.source}`}>
                    <td>{r.step}</td><td>{r.requests}</td><td>{r.items}</td><td>{r.tokens}</td><td>{r.tokens_per_item}</td>
                    <td>{totalTokens ? `${Math.round((r.tokens / totalTokens) * 100)}%` : '–'}</td>
                    <td>{SOURCE_LABELS[r.source] || r.source}</td>
                  </tr>
                ))}
                <tr className={styles.totalRow}>
                  <td>Total</td><td>{breakdown.reduce((s, r) => s + r.requests, 0)}</td><td />
                  <td>{totalTokens}</td><td>{(totalTokens / Math.max(1, summary.returned_count || 0)).toFixed(2)}</td>
                  <td>100%</td><td>per product returned ({summary.returned_count || 0})</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className={styles.caption}>
            Tokens are what Keepa reported charging for each request. Cached rows cost 0.
            {summary.tokens_left !== null && summary.tokens_left !== undefined ? ` ${summary.tokens_left} tokens left.` : ''}
          </p>
        </section>
      )}
    </div>
  );
};

export default ResultsView;
